/**
 * features/products/ProductsPage.tsx
 */

import { useState } from 'react';
import { Package, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { formatMoney } from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { ApiError } from '~/lib/api';
import type { Product } from '~/lib/types';
import { PageHeader, Pagination, Toolbar } from '~/ui/layout';
import { Button, Card, EmptyState, Input, TableSkeleton } from '~/ui/primitives';
import { ConfirmDialog, useToast } from '~/ui/overlays';
import { useDeleteProduct, useProducts } from './hooks';
import { ProductFormDialog } from './ProductFormDialog';

const PAGE_SIZE = 15;

export function ProductsPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState<Product | null>(null);

  const { data, isLoading, isFetching } = useProducts({ page, pageSize: PAGE_SIZE, search });
  const deleteProduct = useDeleteProduct();
  const toast = useToast();

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteProduct.mutateAsync(deleting.id);
      toast.success('ลบสินค้าแล้ว');
      setDeleting(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div>
      <PageHeader
        title="สินค้า/บริการ"
        description="รายการที่ใช้เลือกในใบเสนอราคาและใบกำกับภาษี"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus size={16} />
            เพิ่มสินค้า
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <div className="relative w-full max-w-xs">
            <Search
              size={15}
              className="absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle"
              aria-hidden
            />
            <Input
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setPage(1);
              }}
              placeholder="ค้นหาชื่อ, รหัสสินค้า"
              className="pl-8"
            />
          </div>
          {isFetching && !isLoading ? <span className="text-xs text-fg-subtle">กำลังอัปเดต…</span> : null}
        </Toolbar>

        {isLoading ? (
          <TableSkeleton columns={4} />
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            icon={<Package size={28} />}
            title={search ? 'ไม่พบสินค้าที่ค้นหา' : 'ยังไม่มีสินค้า/บริการ'}
            description={
              search ? undefined : 'สินค้าใหม่จะถูกเพิ่มอัตโนมัติเมื่อกรอกในใบเสนอราคาด้วย'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>ชื่อสินค้า/บริการ</th>
                    <th>หน่วย</th>
                    <th className="text-right">ราคา/หน่วย</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <p className="font-medium text-fg">{product.name}</p>
                        {product.code ? (
                          <p className="text-xs text-fg-muted">รหัส {product.code}</p>
                        ) : null}
                      </td>
                      <td className="text-fg-muted">{product.unit || '-'}</td>
                      <td className="tabular text-right text-fg">{formatMoney(product.price)}</td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="แก้ไข"
                            onClick={() => {
                              setEditing(product);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="ลบ"
                            onClick={() => setDeleting(product)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              pageSize={data.pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <ProductFormDialog open={formOpen} onClose={() => setFormOpen(false)} product={editing} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => void handleDelete()}
        title="ลบสินค้า/บริการ"
        message={`ต้องการลบ "${deleting?.name}" ใช่หรือไม่ การลบไม่สามารถย้อนกลับได้`}
        confirmLabel="ลบ"
        loading={deleteProduct.isPending}
      />
    </div>
  );
}
